import { Schema } from "effect";
import {
  CompleteAgentRunRequestFields,
  completeAgentRunResponseStatusConsistency,
} from "./agent-storage";

export const AgentCoordinationBoundaryParseOptions = {
  errors: "all",
  onExcessProperty: "error",
  reportInput: false,
} as const;

const IdentifierSchema = Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(180));
const ShortErrorCodeSchema = Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(120));
const SafeMessageSchema = Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(500));
const NonNegativeSafeIntegerSchema = Schema.Int.check(
  Schema.isGreaterThanOrEqualTo(0),
  Schema.isLessThanOrEqualTo(Number.MAX_SAFE_INTEGER),
);
const PositiveSafeIntegerSchema = NonNegativeSafeIntegerSchema.check(
  Schema.isGreaterThanOrEqualTo(1),
);
const AttemptGenerationSchema = PositiveSafeIntegerSchema;
const LeaseDurationMsSchema = Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 120_000 }));

const AttemptIdentityFields = {
  workspaceId: IdentifierSchema,
  runId: IdentifierSchema,
  attemptId: IdentifierSchema,
} as const;

const AttemptFenceFields = {
  ...AttemptIdentityFields,
  runGeneration: AttemptGenerationSchema,
  attemptGeneration: AttemptGenerationSchema,
  leaseId: IdentifierSchema,
  leaseOwner: IdentifierSchema,
} as const;

const WithCoordinatorTime = {
  now: NonNegativeSafeIntegerSchema,
} as const;

export const AcquireAgentAttemptLeaseInputSchema = Schema.Struct({
  ...AttemptIdentityFields,
  expectedRunGeneration: AttemptGenerationSchema,
  expectedAttemptGeneration: AttemptGenerationSchema,
  leaseId: IdentifierSchema,
  leaseOwner: IdentifierSchema,
  leaseDurationMs: LeaseDurationMsSchema,
  providerCostBudgetMicros: NonNegativeSafeIntegerSchema,
});

export const AcquireAgentAttemptLeaseRequestSchema = Schema.Struct({
  ...AcquireAgentAttemptLeaseInputSchema.fields,
  ...WithCoordinatorTime,
});

export const AcquireAgentAttemptLeaseResultSchema = Schema.Struct({
  runGeneration: AttemptGenerationSchema,
  attemptGeneration: AttemptGenerationSchema,
  leaseExpiresAt: NonNegativeSafeIntegerSchema,
  providerCostBudgetMicros: NonNegativeSafeIntegerSchema,
});

export const RenewAgentAttemptLeaseInputSchema = Schema.Struct({
  ...AttemptFenceFields,
  leaseDurationMs: LeaseDurationMsSchema,
});

export const RenewAgentAttemptLeaseRequestSchema = Schema.Struct({
  ...RenewAgentAttemptLeaseInputSchema.fields,
  ...WithCoordinatorTime,
});

export const RenewAgentAttemptLeaseResultSchema = Schema.Struct({
  leaseExpiresAt: NonNegativeSafeIntegerSchema,
});

export const AuthorizeAgentBrowserToolInputSchema = Schema.Struct({
  ...AttemptFenceFields,
  projectId: IdentifierSchema,
  threadId: IdentifierSchema,
});

export const AuthorizeAgentBrowserToolRequestSchema = Schema.Struct({
  ...AuthorizeAgentBrowserToolInputSchema.fields,
  ...WithCoordinatorTime,
});

export const AuthorizeAgentBrowserToolResultSchema = Schema.Struct({
  status: Schema.Literal("authorized"),
});

export const ReleaseAgentAttemptLeaseInputSchema = Schema.Struct(AttemptFenceFields);

export const ReleaseAgentAttemptLeaseRequestSchema = Schema.Struct({
  ...ReleaseAgentAttemptLeaseInputSchema.fields,
  ...WithCoordinatorTime,
});

export const ReleaseAgentAttemptLeaseResultSchema = Schema.Struct({
  status: Schema.Literal("released"),
});

export const RequestAgentRunCancellationInputSchema = Schema.Struct(AttemptFenceFields);

export const RequestAgentRunCancellationRequestSchema = Schema.Struct({
  ...RequestAgentRunCancellationInputSchema.fields,
  ...WithCoordinatorTime,
});

export const RequestAgentRunCancellationResultSchema = Schema.Struct({
  status: Schema.Literal("cancellation-requested"),
  cancellationRequestedAt: NonNegativeSafeIntegerSchema,
});

const CommitAgentAttemptTerminalInputFields = {
  ...CompleteAgentRunRequestFields,
  runGeneration: AttemptGenerationSchema,
  leaseId: IdentifierSchema,
  leaseOwner: IdentifierSchema,
} as const;

export const CommitAgentAttemptTerminalInputSchema = Schema.Struct(
  CommitAgentAttemptTerminalInputFields,
).check(Schema.makeFilter(completeAgentRunResponseStatusConsistency));

export const CommitAgentAttemptTerminalRequestSchema = Schema.Struct({
  ...CommitAgentAttemptTerminalInputFields,
  ...WithCoordinatorTime,
}).check(Schema.makeFilter(completeAgentRunResponseStatusConsistency));

export const CommitAgentAttemptTerminalResultSchema = Schema.Struct({
  status: Schema.Literals(["succeeded", "failed", "cancelled"]),
});

export const AgentModelCallStatusSchema = Schema.Literals([
  "claimed",
  "dispatching",
  "completed",
  "failed",
  "cancelled",
  "uncertain",
]);

export const ClaimAgentModelCallInputSchema = Schema.Struct({
  ...AttemptFenceFields,
  claimKey: IdentifierSchema,
  sdkStepNumber: Schema.optionalKey(NonNegativeSafeIntegerSchema),
  provider: IdentifierSchema,
  model: IdentifierSchema,
  reasoning: Schema.optionalKey(Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(80))),
  reservedProviderCostMicros: NonNegativeSafeIntegerSchema,
});

export const ClaimAgentModelCallRequestSchema = Schema.Struct({
  ...ClaimAgentModelCallInputSchema.fields,
  ...WithCoordinatorTime,
});

export const ClaimAgentModelCallResultSchema = Schema.Struct({
  modelCallId: IdentifierSchema,
  callOrdinal: NonNegativeSafeIntegerSchema,
  status: AgentModelCallStatusSchema,
  reservedProviderCostMicros: NonNegativeSafeIntegerSchema,
});

export const StartAgentModelCallInputSchema = Schema.Struct({
  ...AttemptFenceFields,
  modelCallId: IdentifierSchema,
});

export const StartAgentModelCallRequestSchema = Schema.Struct({
  ...StartAgentModelCallInputSchema.fields,
  ...WithCoordinatorTime,
});

export const StartAgentModelCallResultSchema = Schema.Struct({
  status: Schema.Literal("dispatching"),
  dispatchStartedAt: NonNegativeSafeIntegerSchema,
});

export const SettleAgentModelCallInputSchema = Schema.Struct({
  ...AttemptFenceFields,
  modelCallId: IdentifierSchema,
  status: Schema.Literals(["completed", "failed", "cancelled"]),
  actualProviderCostMicros: NonNegativeSafeIntegerSchema,
  inputTokens: Schema.optionalKey(NonNegativeSafeIntegerSchema),
  outputTokens: Schema.optionalKey(NonNegativeSafeIntegerSchema),
  totalTokens: Schema.optionalKey(NonNegativeSafeIntegerSchema),
  errorCode: Schema.optionalKey(ShortErrorCodeSchema),
  errorMessage: Schema.optionalKey(SafeMessageSchema),
});

export const SettleAgentModelCallRequestSchema = Schema.Struct({
  ...SettleAgentModelCallInputSchema.fields,
  ...WithCoordinatorTime,
});

export const SettleAgentModelCallResultSchema = Schema.Struct({
  status: Schema.Literals(["completed", "failed", "cancelled"]),
  actualProviderCostMicros: NonNegativeSafeIntegerSchema,
});

export const MarkAgentModelCallOutcomeUnknownInputSchema = Schema.Struct({
  ...AttemptFenceFields,
  modelCallId: IdentifierSchema,
  errorCode: ShortErrorCodeSchema,
  errorMessage: Schema.optionalKey(SafeMessageSchema),
});

export const MarkAgentModelCallOutcomeUnknownRequestSchema = Schema.Struct({
  ...MarkAgentModelCallOutcomeUnknownInputSchema.fields,
  ...WithCoordinatorTime,
});

export const MarkAgentModelCallOutcomeUnknownResultSchema = Schema.Struct({
  status: Schema.Literal("uncertain"),
  reservedProviderCostMicros: NonNegativeSafeIntegerSchema,
});

export const AgentCoordinationPublicErrorCodeSchema = Schema.Literals([
  "AGENT_COORDINATION_INVALID_INPUT",
  "AGENT_COORDINATION_UNAUTHENTICATED",
  "AGENT_COORDINATION_FORBIDDEN",
  "AGENT_COORDINATION_NOT_FOUND",
  "AGENT_ATTEMPT_STALE",
  "AGENT_ATTEMPT_CANCELLED",
  "AGENT_ATTEMPT_TERMINAL",
  "AGENT_ATTEMPT_LEASE_EXPIRED",
  "AGENT_ATTEMPT_LEASE_LOST",
  "AGENT_ATTEMPT_CALLS_UNSETTLED",
  "AGENT_MODEL_CALL_BUDGET_DENIED",
  "AGENT_DELEGATION_DISABLED",
  "AGENT_DELEGATION_LIMIT_REACHED",
  "AGENT_DELEGATION_BUDGET_DENIED",
  "AGENT_DELEGATION_RESULT_INVALID",
  "AGENT_ATTEMPT_NOT_READY",
  "AGENT_COORDINATION_CONFLICT",
  "AGENT_COORDINATION_UNAVAILABLE",
]);

export const AgentCoordinationPublicErrorSchema = Schema.Struct({
  code: AgentCoordinationPublicErrorCodeSchema,
  message: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(240)),
  retryable: Schema.Boolean,
});

export const AgentCoordinationInternalErrorDataSchema = Schema.Struct({
  code: Schema.Literals([
    "UNAUTHENTICATED",
    "FORBIDDEN",
    "INVALID_INPUT",
    "NOT_FOUND",
    "STALE_ATTEMPT",
    "ATTEMPT_CANCELLED",
    "ATTEMPT_TERMINAL",
    "ATTEMPT_LEASE_LOST",
    "ATTEMPT_LEASE_EXPIRED",
    "ATTEMPT_CALLS_UNSETTLED",
    "ATTEMPT_LEASE_CONFLICT",
    "ATTEMPT_LEASE_HELD",
    "ATTEMPT_BUDGET_CONFLICT",
    "RUN_TERMINAL",
    "RUN_ALREADY_STARTED",
    "THREAD_RUN_ACTIVE",
    "ARCHIVED",
    "ENTITLEMENTS_UNAVAILABLE",
    "RUN_COMPLETION_CONFLICT",
    "MESSAGE_ID_CONFLICT",
    "EVENT_SEQUENCE_CONFLICT",
    "EVENT_SEQUENCE_GAP",
    "EVENT_ID_CONFLICT",
    "MODEL_CALL_NOT_FOUND",
    "MODEL_CALL_CLAIM_CONFLICT",
    "MODEL_CALL_BUDGET_UNAVAILABLE",
    "MODEL_CALL_BUDGET_DENIED",
    "MODEL_CALL_WORKSPACE_BUDGET_DENIED",
    "MODEL_CALL_TERMINAL",
    "MODEL_CALL_SETTLEMENT_CONFLICT",
    "MODEL_CALL_NOT_DISPATCHED",
    "MODEL_CALL_BUDGET_EXCEEDED",
    "MODEL_CALL_LEDGER_CONFLICT",
    "MODEL_CALL_WORKSPACE_BUDGET_CONFLICT",
    "MODEL_CALL_OUTCOME_CONFLICT",
    "DELEGATION_DISABLED",
    "DELEGATION_LIMIT_REACHED",
    "DELEGATION_BUDGET_UNAVAILABLE",
    "DELEGATION_NOT_FOUND",
    "DELEGATION_RESULT_INVALID",
    "ATTEMPT_NOT_READY",
  ]),
  message: SafeMessageSchema,
});

export const decodeAcquireAgentAttemptLeaseRequest = Schema.decodeUnknownSync(
  AcquireAgentAttemptLeaseRequestSchema,
  AgentCoordinationBoundaryParseOptions,
);
export const decodeAcquireAgentAttemptLeaseResult = Schema.decodeUnknownSync(
  AcquireAgentAttemptLeaseResultSchema,
  AgentCoordinationBoundaryParseOptions,
);
export const decodeRenewAgentAttemptLeaseRequest = Schema.decodeUnknownSync(
  RenewAgentAttemptLeaseRequestSchema,
  AgentCoordinationBoundaryParseOptions,
);
export const decodeRenewAgentAttemptLeaseResult = Schema.decodeUnknownSync(
  RenewAgentAttemptLeaseResultSchema,
  AgentCoordinationBoundaryParseOptions,
);
export const decodeAuthorizeAgentBrowserToolRequest = Schema.decodeUnknownSync(
  AuthorizeAgentBrowserToolRequestSchema,
  AgentCoordinationBoundaryParseOptions,
);
export const decodeAuthorizeAgentBrowserToolResult = Schema.decodeUnknownSync(
  AuthorizeAgentBrowserToolResultSchema,
  AgentCoordinationBoundaryParseOptions,
);
export const decodeReleaseAgentAttemptLeaseRequest = Schema.decodeUnknownSync(
  ReleaseAgentAttemptLeaseRequestSchema,
  AgentCoordinationBoundaryParseOptions,
);
export const decodeReleaseAgentAttemptLeaseResult = Schema.decodeUnknownSync(
  ReleaseAgentAttemptLeaseResultSchema,
  AgentCoordinationBoundaryParseOptions,
);
export const decodeRequestAgentRunCancellationRequest = Schema.decodeUnknownSync(
  RequestAgentRunCancellationRequestSchema,
  AgentCoordinationBoundaryParseOptions,
);
export const decodeRequestAgentRunCancellationResult = Schema.decodeUnknownSync(
  RequestAgentRunCancellationResultSchema,
  AgentCoordinationBoundaryParseOptions,
);
export const decodeCommitAgentAttemptTerminalRequest = Schema.decodeUnknownSync(
  CommitAgentAttemptTerminalRequestSchema,
  AgentCoordinationBoundaryParseOptions,
);
export const decodeCommitAgentAttemptTerminalResult = Schema.decodeUnknownSync(
  CommitAgentAttemptTerminalResultSchema,
  AgentCoordinationBoundaryParseOptions,
);
export const decodeClaimAgentModelCallRequest = Schema.decodeUnknownSync(
  ClaimAgentModelCallRequestSchema,
  AgentCoordinationBoundaryParseOptions,
);
export const decodeClaimAgentModelCallResult = Schema.decodeUnknownSync(
  ClaimAgentModelCallResultSchema,
  AgentCoordinationBoundaryParseOptions,
);
export const decodeStartAgentModelCallRequest = Schema.decodeUnknownSync(
  StartAgentModelCallRequestSchema,
  AgentCoordinationBoundaryParseOptions,
);
export const decodeStartAgentModelCallResult = Schema.decodeUnknownSync(
  StartAgentModelCallResultSchema,
  AgentCoordinationBoundaryParseOptions,
);
export const decodeSettleAgentModelCallRequest = Schema.decodeUnknownSync(
  SettleAgentModelCallRequestSchema,
  AgentCoordinationBoundaryParseOptions,
);
export const decodeSettleAgentModelCallResult = Schema.decodeUnknownSync(
  SettleAgentModelCallResultSchema,
  AgentCoordinationBoundaryParseOptions,
);
export const decodeMarkAgentModelCallOutcomeUnknownRequest = Schema.decodeUnknownSync(
  MarkAgentModelCallOutcomeUnknownRequestSchema,
  AgentCoordinationBoundaryParseOptions,
);
export const decodeMarkAgentModelCallOutcomeUnknownResult = Schema.decodeUnknownSync(
  MarkAgentModelCallOutcomeUnknownResultSchema,
  AgentCoordinationBoundaryParseOptions,
);
export const decodeAgentCoordinationPublicError = Schema.decodeUnknownSync(
  AgentCoordinationPublicErrorSchema,
  AgentCoordinationBoundaryParseOptions,
);
export const decodeAgentCoordinationInternalErrorData = Schema.decodeUnknownSync(
  AgentCoordinationInternalErrorDataSchema,
  AgentCoordinationBoundaryParseOptions,
);

export const encodeAcquireAgentAttemptLeaseRequest = Schema.encodeSync(
  AcquireAgentAttemptLeaseRequestSchema,
  AgentCoordinationBoundaryParseOptions,
);
export const encodeRenewAgentAttemptLeaseRequest = Schema.encodeSync(
  RenewAgentAttemptLeaseRequestSchema,
  AgentCoordinationBoundaryParseOptions,
);
export const encodeAuthorizeAgentBrowserToolRequest = Schema.encodeSync(
  AuthorizeAgentBrowserToolRequestSchema,
  AgentCoordinationBoundaryParseOptions,
);
export const encodeAuthorizeAgentBrowserToolResult = Schema.encodeSync(
  AuthorizeAgentBrowserToolResultSchema,
  AgentCoordinationBoundaryParseOptions,
);
export const encodeReleaseAgentAttemptLeaseRequest = Schema.encodeSync(
  ReleaseAgentAttemptLeaseRequestSchema,
  AgentCoordinationBoundaryParseOptions,
);
export const encodeRequestAgentRunCancellationRequest = Schema.encodeSync(
  RequestAgentRunCancellationRequestSchema,
  AgentCoordinationBoundaryParseOptions,
);
export const encodeCommitAgentAttemptTerminalRequest = Schema.encodeSync(
  CommitAgentAttemptTerminalRequestSchema,
  AgentCoordinationBoundaryParseOptions,
);
export const encodeClaimAgentModelCallRequest = Schema.encodeSync(
  ClaimAgentModelCallRequestSchema,
  AgentCoordinationBoundaryParseOptions,
);
export const encodeStartAgentModelCallRequest = Schema.encodeSync(
  StartAgentModelCallRequestSchema,
  AgentCoordinationBoundaryParseOptions,
);
export const encodeSettleAgentModelCallRequest = Schema.encodeSync(
  SettleAgentModelCallRequestSchema,
  AgentCoordinationBoundaryParseOptions,
);
export const encodeMarkAgentModelCallOutcomeUnknownRequest = Schema.encodeSync(
  MarkAgentModelCallOutcomeUnknownRequestSchema,
  AgentCoordinationBoundaryParseOptions,
);

export type AcquireAgentAttemptLeaseInput = typeof AcquireAgentAttemptLeaseInputSchema.Type;
export type AcquireAgentAttemptLeaseRequest = typeof AcquireAgentAttemptLeaseRequestSchema.Type;
export type AcquireAgentAttemptLeaseRequestEncoded =
  typeof AcquireAgentAttemptLeaseRequestSchema.Encoded;
export type AcquireAgentAttemptLeaseResult = typeof AcquireAgentAttemptLeaseResultSchema.Type;
export type RenewAgentAttemptLeaseInput = typeof RenewAgentAttemptLeaseInputSchema.Type;
export type RenewAgentAttemptLeaseRequest = typeof RenewAgentAttemptLeaseRequestSchema.Type;
export type RenewAgentAttemptLeaseRequestEncoded =
  typeof RenewAgentAttemptLeaseRequestSchema.Encoded;
export type RenewAgentAttemptLeaseResult = typeof RenewAgentAttemptLeaseResultSchema.Type;
export type AuthorizeAgentBrowserToolInput = typeof AuthorizeAgentBrowserToolInputSchema.Type;
export type AuthorizeAgentBrowserToolRequest = typeof AuthorizeAgentBrowserToolRequestSchema.Type;
export type AuthorizeAgentBrowserToolRequestEncoded =
  typeof AuthorizeAgentBrowserToolRequestSchema.Encoded;
export type AuthorizeAgentBrowserToolResult = typeof AuthorizeAgentBrowserToolResultSchema.Type;
export type ReleaseAgentAttemptLeaseInput = typeof ReleaseAgentAttemptLeaseInputSchema.Type;
export type ReleaseAgentAttemptLeaseRequest = typeof ReleaseAgentAttemptLeaseRequestSchema.Type;
export type ReleaseAgentAttemptLeaseRequestEncoded =
  typeof ReleaseAgentAttemptLeaseRequestSchema.Encoded;
export type ReleaseAgentAttemptLeaseResult = typeof ReleaseAgentAttemptLeaseResultSchema.Type;
export type RequestAgentRunCancellationInput = typeof RequestAgentRunCancellationInputSchema.Type;
export type RequestAgentRunCancellationRequest =
  typeof RequestAgentRunCancellationRequestSchema.Type;
export type RequestAgentRunCancellationRequestEncoded =
  typeof RequestAgentRunCancellationRequestSchema.Encoded;
export type RequestAgentRunCancellationResult = typeof RequestAgentRunCancellationResultSchema.Type;
export type CommitAgentAttemptTerminalInput = typeof CommitAgentAttemptTerminalInputSchema.Type;
export type CommitAgentAttemptTerminalRequest = typeof CommitAgentAttemptTerminalRequestSchema.Type;
export type CommitAgentAttemptTerminalRequestEncoded =
  typeof CommitAgentAttemptTerminalRequestSchema.Encoded;
export type CommitAgentAttemptTerminalResult = typeof CommitAgentAttemptTerminalResultSchema.Type;
export type AgentModelCallStatus = typeof AgentModelCallStatusSchema.Type;
export type ClaimAgentModelCallInput = typeof ClaimAgentModelCallInputSchema.Type;
export type ClaimAgentModelCallRequest = typeof ClaimAgentModelCallRequestSchema.Type;
export type ClaimAgentModelCallRequestEncoded = typeof ClaimAgentModelCallRequestSchema.Encoded;
export type ClaimAgentModelCallResult = typeof ClaimAgentModelCallResultSchema.Type;
export type StartAgentModelCallInput = typeof StartAgentModelCallInputSchema.Type;
export type StartAgentModelCallRequest = typeof StartAgentModelCallRequestSchema.Type;
export type StartAgentModelCallRequestEncoded = typeof StartAgentModelCallRequestSchema.Encoded;
export type StartAgentModelCallResult = typeof StartAgentModelCallResultSchema.Type;
export type SettleAgentModelCallInput = typeof SettleAgentModelCallInputSchema.Type;
export type SettleAgentModelCallRequest = typeof SettleAgentModelCallRequestSchema.Type;
export type SettleAgentModelCallRequestEncoded = typeof SettleAgentModelCallRequestSchema.Encoded;
export type SettleAgentModelCallResult = typeof SettleAgentModelCallResultSchema.Type;
export type MarkAgentModelCallOutcomeUnknownInput =
  typeof MarkAgentModelCallOutcomeUnknownInputSchema.Type;
export type MarkAgentModelCallOutcomeUnknownRequest =
  typeof MarkAgentModelCallOutcomeUnknownRequestSchema.Type;
export type MarkAgentModelCallOutcomeUnknownRequestEncoded =
  typeof MarkAgentModelCallOutcomeUnknownRequestSchema.Encoded;
export type MarkAgentModelCallOutcomeUnknownResult =
  typeof MarkAgentModelCallOutcomeUnknownResultSchema.Type;
export type AgentCoordinationPublicErrorCode = typeof AgentCoordinationPublicErrorCodeSchema.Type;
export type AgentCoordinationPublicError = typeof AgentCoordinationPublicErrorSchema.Type;
